import { useEffect, useState } from "react";
import "./patientReceiptStyles.css";
import converter from "number-to-words";
import { useQuery } from "@tanstack/react-query";
import { fetchPatientReceiptData } from "services/patientappointmentdetails";
import { DEFAULT_TOTAL_RECORDS, STALE_TIME } from "utils/constants";
import usePagination from "hooks/usePagination";
import Loader from "components/Common/loader";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Footer from "../../assets/images/footer_line.png";
import zoneLocalStorageId from "utils/zone";
import { fetchZoneLogoById } from "services/zone";

const PatientReceiptCreditMode = () => {
  const { currentPage, updateTotalRecords } = usePagination();
  const [billAmt, setBillAmt] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const receiptData = JSON.parse(localStorage.getItem("receiptPatient"));

  const formatUnixTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options).replace(",", "");
  };

  function convertEpochToDate(epoch) {
    if (!epoch) return "";
    const date = new Date(epoch * 1000);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const { data: zoneData, isLoading: isZoneLoading } = useQuery(
    ["fetch-zone-logo-by-id", zoneLocalStorageId()],
    () => fetchZoneLogoById(zoneLocalStorageId()),
    {
      enabled: !!zoneLocalStorageId(),
      staleTime: STALE_TIME,
      select: (res) => res || [],
      onError: (err) => console.error("Error fetching zone data:", err),
    },
  );

  const { data: cashReceiptData, isLoading } = useQuery(
    ["fetch-cash-patient-receipt"],
    () => fetchPatientReceiptData({ appointment_id: receiptData }),
    {
      staleTime: STALE_TIME,
      onSuccess: (res) => {
        updateTotalRecords(res?.data?.additional_data?.total_count);
      },
      select: (res) => {
        const response = res?.data?.data;
        if (response && response.length) {
          res.data.data = response.map((x, index) => ({
            ...x,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          }));
        }
        return res;
      },
      onError: (res) => console.error("Error fetching payments:", res),
      enabled: !!receiptData,
    },
  );

  useEffect(() => {
    if (cashReceiptData && cashReceiptData.data[0]) {
      const appointment_investigation_obj =
        cashReceiptData.data[0].appointment_investigation_obj;
      const uniquePackages = {};
      let noPackageTotal = 0;

      appointment_investigation_obj.forEach((item) => {
        const packageId = item.package_id;
        const cost = item.package?.package_cost || item.mrp || 0;
        if (packageId) {
          if (!uniquePackages[packageId]) uniquePackages[packageId] = cost;
        } else {
          noPackageTotal += cost;
        }
      });

      const packageTotal = Object.values(uniquePackages).reduce(
        (sum, cost) => sum + cost,
        0,
      );
      setBillAmt(packageTotal + noPackageTotal);
    }
  }, [cashReceiptData]);

  if (!cashReceiptData || isLoading) {
    return <Loader isLoading={isLoading} />;
  }

  function convertSecondsToAge(seconds) {
    const secondsInYear = 31536000;
    const secondsInMonth = 2629746;
    const secondsInDay = 86400;
    const years = Math.floor(seconds / secondsInYear);
    seconds %= secondsInYear;
    const months = Math.floor(seconds / secondsInMonth);
    seconds %= secondsInMonth;
    const days = Math.floor(seconds / secondsInDay);
    return { years, months, days };
  }

  const {
    appointment_detail_obj = [],
    appointment_investigation_obj = [],
    patient_obj = {},
    zone_obj = {},
    franchise_obj = {},
  } = cashReceiptData?.data[0] || {};

  const rowCashReceiptData = cashReceiptData?.data[0];
  const ageDetails = convertSecondsToAge(patient_obj?.age || 0);

  const capitalizeWords = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  const downloadPDF = async () => {
    try {
      setIsDownloading(true);

      const BRAND_RGB = [91, 175, 214];
      const mobile = rowCashReceiptData?.zone_obj?.mobile_number || "";
      const email = rowCashReceiptData?.zone_obj?.email || "";
      const website = rowCashReceiptData?.zone_obj?.website || "";
      const address = rowCashReceiptData?.zone_obj?.address || "";
      const footerBarH = address ? 18 : 12;

      const downloadBtn = document.querySelector(".download-btn");
      const downloadBtnPlain = document.querySelector(".download-btn-plain");
      const footerSection = document.querySelector(".receipt-footer-section");
      if (downloadBtn) downloadBtn.style.display = "none";
      if (downloadBtnPlain) downloadBtnPlain.style.display = "none";
      if (footerSection) footerSection.style.display = "none";

      await new Promise((r) => setTimeout(r, 100));

      const headerEl = document.querySelector(".receipt-header-section");
      if (!headerEl) throw new Error("Header not found");
      const origHW = headerEl.style.width;
      headerEl.style.width = "794px";
      await new Promise((r) => setTimeout(r, 80));
      const headerCanvas = await html2canvas(headerEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        windowWidth: 1000,
        scrollX: 0,
        scrollY: -headerEl.getBoundingClientRect().top,
        width: 794,
        height: headerEl.scrollHeight,
      });
      headerEl.style.width = origHW;
      const headerImgData = headerCanvas.toDataURL("image/png", 1.0);

      headerEl.style.display = "none";

      const receiptEl = document.querySelector(".receipt-inner-unique");
      if (!receiptEl) throw new Error("Receipt element not found");
      const origW = receiptEl.style.width;
      const origMW = receiptEl.style.minWidth;
      receiptEl.style.width = "794px";
      receiptEl.style.minWidth = "794px";
      await new Promise((r) => setTimeout(r, 150));
      window.scrollTo(0, 0);

      const bodyCanvas = await html2canvas(receiptEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        windowWidth: 1000,
        scrollX: 0,
        scrollY: -receiptEl.getBoundingClientRect().top,
        x: 0,
        y: 0,
        width: 794,
        height: receiptEl.scrollHeight,
      });

      receiptEl.style.width = origW;
      receiptEl.style.minWidth = origMW;
      headerEl.style.display = "";
      if (footerSection) footerSection.style.display = "block";
      if (downloadBtn) downloadBtn.style.display = "block";
      if (downloadBtnPlain) downloadBtnPlain.style.display = "block";

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();

      const headerH = (headerCanvas.height / headerCanvas.width) * pdfW;
      const bodyTotalH = (bodyCanvas.height / bodyCanvas.width) * pdfW;
      const slicePerPage = pdfH - headerH;
      const sliceLastPage = pdfH - headerH - footerBarH;
      const pxPerMM = bodyCanvas.width / pdfW;

      const drawHeader = () => {
        pdf.addImage(headerImgData, "PNG", 0, 0, pdfW, headerH);
      };

      const drawFooter = () => {
        const barY = pdfH - footerBarH;
        pdf.setFillColor(...BRAND_RGB);
        pdf.rect(0, barY, pdfW, footerBarH, "F");
        pdf.setDrawColor(255, 255, 255);
        pdf.setLineWidth(0.3);
        pdf.line(0, barY, pdfW, barY);

        const items = [];
        if (mobile) items.push({ l: "M", v: mobile });
        if (email) items.push({ l: "E", v: email });
        if (website) items.push({ l: "W", v: website });

        if (items.length > 0) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8.5);
          const circleW = 5;
          pdf.setFont("helvetica", "bold");
          const sepW = pdf.getTextWidth("  |  ");
          pdf.setFont("helvetica", "normal");
          const totalW = items.reduce(
            (sum, it, i) =>
              sum +
              circleW +
              pdf.getTextWidth(it.v) +
              (i < items.length - 1 ? sepW : 0),
            0,
          );

          let curX = (pdfW - totalW) / 2;
          const rowY = barY + 7;
          items.forEach((it, i) => {
            pdf.setFillColor(255, 255, 255);
            pdf.circle(curX + 1.8, rowY - 1.2, 1.8, "F");
            pdf.setTextColor(...BRAND_RGB);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(6);
            pdf.text(it.l, curX + 0.9, rowY - 0.3);
            pdf.setTextColor(255, 255, 255);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(8.5);
            pdf.text(it.v, curX + 4.6, rowY);
            curX += circleW + pdf.getTextWidth(it.v);
            if (i < items.length - 1) {
              pdf.setFont("helvetica", "bold");
              pdf.text("  |  ", curX, rowY);
              curX += sepW;
            }
          });
        }

        if (address) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(255, 255, 255);
          const addrLine = pdf.splitTextToSize(
            `Addr: ${address}`,
            pdfW - 20,
          )[0];
          pdf.text(
            addrLine,
            (pdfW - pdf.getTextWidth(addrLine)) / 2,
            barY + 14,
          );
        }
        pdf.setTextColor(0, 0, 0);
      };

      let bodyOffsetMM = 0;
      let pageNum = 0;

      while (bodyOffsetMM < bodyTotalH) {
        if (pageNum > 0) pdf.addPage();
        pageNum++;

        const isLast = bodyOffsetMM + slicePerPage >= bodyTotalH;
        const sliceH = isLast
          ? Math.min(sliceLastPage, bodyTotalH - bodyOffsetMM)
          : slicePerPage;

        const srcY = Math.round(bodyOffsetMM * pxPerMM);
        const srcH = Math.round(sliceH * pxPerMM);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = bodyCanvas.width;
        sliceCanvas.height = srcH;
        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(
          bodyCanvas,
          0,
          srcY,
          bodyCanvas.width,
          srcH,
          0,
          0,
          bodyCanvas.width,
          srcH,
        );
        const sliceData = sliceCanvas.toDataURL("image/png", 1.0);

        drawHeader();
        pdf.addImage(sliceData, "PNG", 0, headerH, pdfW, sliceH);
        if (isLast) drawFooter();

        bodyOffsetMM += slicePerPage;
      }

      const patientName = patient_obj?.patient_name || "Patient";
      const currentDate = new Date().toISOString().split("T")[0];
      pdf.save(
        `Receipt_${patientName.replace(/\s+/g, "_")}_${currentDate}.pdf`,
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsDownloading(false);
      const downloadBtn = document.querySelector(".download-btn");
      const downloadBtnPlain = document.querySelector(".download-btn-plain");
      const footerSection = document.querySelector(".receipt-footer-section");
      const headerEl = document.querySelector(".receipt-header-section");
      if (downloadBtn) downloadBtn.style.display = "block";
      if (downloadBtnPlain) downloadBtnPlain.style.display = "block";
      if (footerSection) footerSection.style.display = "block";
      if (headerEl) headerEl.style.display = "";
    }
  };
  const downloadPDFWithoutHeaderFooter = async () => {
    try {
      setIsDownloading(true);

      const downloadBtn = document.querySelector(".download-btn");
      const downloadBtnPlain = document.querySelector(".download-btn-plain");
      const footerSection = document.querySelector(".receipt-footer-section");
      const headerEl = document.querySelector(".receipt-header-section");

      if (downloadBtn) downloadBtn.style.display = "none";
      if (downloadBtnPlain) downloadBtnPlain.style.display = "none";
      if (footerSection) footerSection.style.display = "none";

      const headerH_px = headerEl ? headerEl.offsetHeight : 0;

      if (headerEl) headerEl.style.visibility = "hidden";

      const receiptEl = document.querySelector(".receipt-inner-unique");
      if (!receiptEl) throw new Error("Receipt element not found");

      const origW = receiptEl.style.width;
      const origMW = receiptEl.style.minWidth;
      receiptEl.style.width = "794px";
      receiptEl.style.minWidth = "794px";
      await new Promise((r) => setTimeout(r, 150));
      window.scrollTo(0, 0);

      const bodyCanvas = await html2canvas(receiptEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        windowWidth: 1000,
        scrollX: 0,
        scrollY: -receiptEl.getBoundingClientRect().top,
        x: 0,
        y: 0,
        width: 794,
        height: receiptEl.scrollHeight,
      });

      receiptEl.style.width = origW;
      receiptEl.style.minWidth = origMW;
      if (headerEl) headerEl.style.visibility = "";
      if (footerSection) footerSection.style.display = "block";
      if (downloadBtn) downloadBtn.style.display = "block";
      if (downloadBtnPlain) downloadBtnPlain.style.display = "block";

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const pxPerMM = bodyCanvas.width / pdfW;

      const canvasScale = 2;

      const headerSkipPx = headerH_px * canvasScale;
      const usableCanvasHeight = bodyCanvas.height - headerSkipPx;
      const bodyTotalH = (usableCanvasHeight / bodyCanvas.width) * pdfW;

      const headerSpacingMM = (headerH_px / 794) * pdfW;

      let bodyOffsetMM = 0;
      let pageNum = 0;

      while (bodyOffsetMM < bodyTotalH) {
        if (pageNum > 0) pdf.addPage();
        pageNum++;

        const topMargin = pageNum === 1 ? headerSpacingMM : 0;
        const availableH = pdfH - topMargin;
        const sliceH = Math.min(availableH, bodyTotalH - bodyOffsetMM);

        const srcY = headerSkipPx + Math.round(bodyOffsetMM * pxPerMM);
        const srcH = Math.round(sliceH * pxPerMM);

        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = bodyCanvas.width;
        sliceCanvas.height = srcH;
        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(
          bodyCanvas,
          0,
          srcY,
          bodyCanvas.width,
          srcH,
          0,
          0,
          bodyCanvas.width,
          srcH,
        );
        const sliceData = sliceCanvas.toDataURL("image/png", 1.0);

        pdf.addImage(sliceData, "PNG", 0, topMargin, pdfW, sliceH);
        bodyOffsetMM += availableH;
      }

      const patientName = patient_obj?.patient_name || "Patient";
      const currentDate = new Date().toISOString().split("T")[0];
      pdf.save(
        `Receipt_${patientName.replace(/\s+/g, "_")}_${currentDate}_plain.pdf`,
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsDownloading(false);
      const downloadBtn = document.querySelector(".download-btn");
      const downloadBtnPlain = document.querySelector(".download-btn-plain");
      const footerSection = document.querySelector(".receipt-footer-section");
      const headerEl = document.querySelector(".receipt-header-section");
      if (downloadBtn) downloadBtn.style.display = "block";
      if (downloadBtnPlain) downloadBtnPlain.style.display = "block";
      if (footerSection) footerSection.style.display = "block";
      if (headerEl) {
        headerEl.style.visibility = "";
        headerEl.style.display = "";
      }
    }
  };

  const groupedData = appointment_investigation_obj.reduce(
    (acc, item, index) => {
      const packageId = item.package_id;
      if (packageId) {
        if (!acc[packageId]) {
          acc[packageId] = {
            package: item.package,
            investigations: [],
            departments: new Set(),
            delivery_times: new Set(),
            mrp: item.mrp || 0,
            package_cost: item.package?.package_cost || item.mrp || 0,
          };
        }
        acc[packageId].investigations.push(item.investigation);
        if (item.investigation?.lab_department?.department) {
          acc[packageId].departments.add(
            item.investigation.lab_department.department,
          );
        }
        if (item.delivery_time) {
          acc[packageId].delivery_times.add(
            convertEpochToDate(item.delivery_time),
          );
        }
      } else {
        const uniqueKey = `no_package_${index}`;
        acc[uniqueKey] = {
          package: null,
          investigations: [item.investigation],
          departments: new Set(
            item.investigation?.lab_department?.department
              ? [item.investigation.lab_department.department]
              : [],
          ),
          delivery_times: new Set(
            item.delivery_time ? [convertEpochToDate(item.delivery_time)] : [],
          ),
          mrp: item.mrp || 0,
          package_cost: item.mrp || 0,
        };
      }
      return acc;
    },
    {},
  );

  const totalPaidAmount = appointment_detail_obj[0]?.received_amount || 0;

  const BRAND = "#5bafd6";

  const styles = {
    pageWrap: {
      background: "#f0f4f8",
      minHeight: "100vh",
      padding: "16px 12px", // ↓ was 24px 16px
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    card: {
      maxWidth: "794px",
      margin: "0 auto",
      background: "#ffffff",
      borderRadius: "10px", // ↓ was 12px
      boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
      overflow: "hidden",
    },
    header: { background: BRAND, padding: "0", position: "relative" },
    headerTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 20px 8px", // ↓ was 18px 28px 14px
      gap: "10px",
      flexWrap: "wrap",
    },
    logoBox: {
      background: "#ffffff",
      borderRadius: "8px", // ↓ was 10px
      padding: "5px 10px", // ↓ was 8px 14px
      display: "flex",
      alignItems: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    },
    headerCenter: { flex: 1, textAlign: "center" },
    headerTitle: {
      color: "#ffffff",
      fontSize: "17px", // ↓ was 20px
      fontWeight: "800",
      letterSpacing: "2px",
      textTransform: "uppercase",
      margin: 0,
      textShadow: "0 1px 4px rgba(0,0,0,0.15)",
    },
    headerSubtitle: {
      color: "rgba(255,255,255,0.85)",
      fontSize: "10px", // ↓ was 11px
      letterSpacing: "1px",
      marginTop: "2px",
    },
    badgePill: {
      background: "rgba(255,255,255,0.22)",
      border: "1px solid rgba(255,255,255,0.45)",
      color: "#fff",
      borderRadius: "20px",
      padding: "3px 11px", // ↓ was 4px 14px
      fontSize: "10px", // ↓ was 11px
      fontWeight: "600",
      letterSpacing: "0.8px",
      whiteSpace: "nowrap",
    },
    headerDivider: {
      height: "3px", // ↓ was 4px
      background: "linear-gradient(90deg, #f0a500, #ffcd57, #f0a500)",
    },
    body: { padding: "12px 20px" }, // ↓ was 20px 28px
    sectionLabel: {
      fontSize: "9px", // ↓ was 10px
      fontWeight: "700",
      color: BRAND,
      letterSpacing: "1.2px",
      textTransform: "uppercase",
      borderBottom: "2px solid #e5edf7",
      paddingBottom: "3px", // ↓ was 4px
      marginBottom: "6px", // ↓ was 10px
    },
    infoGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "0 24px", // ↓ was 0 32px
      marginBottom: "12px", // ↓ was 20px
    },
    infoRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "3px 0", // ↓ was 5px 0
      borderBottom: "1px dashed #e8edf2",
      fontSize: "11.5px", // ↓ was 13px
      color: "#374151",
    },
    infoLabel: { color: "#6b7280", fontWeight: "500", minWidth: "110px" }, // ↓ was 120px
    infoValue: { fontWeight: "600", color: "#111827", textAlign: "right" },
    tableWrap: {
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      borderRadius: "6px", // ↓ was 8px
      border: "1px solid #dde5f0",
      marginBottom: "12px", // ↓ was 20px
    },
    table: {
      width: "100%",
      minWidth: "700px",
      borderCollapse: "collapse",
      fontSize: "11.5px", // ↓ was 13px
    },
    thead: { background: BRAND },
    th: {
      color: "#fff",
      fontWeight: "600",
      padding: "7px 9px", // ↓ was 10px 12px
      whiteSpace: "nowrap",
      letterSpacing: "0.3px",
      borderRight: "1px solid rgba(255,255,255,0.15)",
    },
    td: {
      padding: "5px 9px", // ↓ was 9px 12px
      borderBottom: "1px solid #e8edf5",
      verticalAlign: "top",
      color: "#374151",
    },
    totalsBox: {
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: "10px", // ↓ was 16px
    },
    totalsInner: {
      width: "100%",
      maxWidth: "320px", // ↓ was 340px
      background: "#f7faff",
      borderRadius: "6px", // ↓ was 8px
      border: "1px solid #dde5f0",
      overflow: "hidden",
    },
    totalRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "5px 12px", // ↓ was 8px 14px
      borderBottom: "1px solid #e8edf5",
      fontSize: "12px", // ↓ was 13.5px
      color: "#374151",
    },
    totalRowFinal: {
      display: "flex",
      justifyContent: "space-between",
      padding: "7px 12px", // ↓ was 10px 14px
      background: BRAND,
      color: "#fff",
      fontWeight: "700",
      fontSize: "13px", // ↓ was 14px
    },
    totalLabel: { color: "#6b7280" },
    totalValue: { fontWeight: "600" },
    wordsStrip: {
      background: "#fffbeb",
      border: "1px solid #fcd34d",
      borderRadius: "6px", // ↓ was 8px
      padding: "6px 12px", // ↓ was 10px 16px
      fontSize: "11.5px", // ↓ was 13px
      color: "#92400e",
      fontWeight: "600",
      marginBottom: "10px", // ↓ was 16px
    },
    noteBox: {
      background: "#f0f9ff",
      border: "1px solid #bae6fd",
      borderRadius: "6px", // ↓ was 8px
      padding: "6px 12px", // ↓ was 9px 14px
      fontSize: "11px", // ↓ was 12px
      color: "#0369a1",
      marginBottom: "10px", // ↓ was 16px
    },
    metaRow: {
      display: "flex",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "6px",
      fontSize: "11px", // ↓ was 12px
      color: "#6b7280",
      marginBottom: "6px", // ↓ was 8px
    },
    metaItem: { display: "flex", gap: "4px" },
    metaValue: { fontWeight: "600", color: "#374151" },
    footer: { background: BRAND, padding: "10px 20px 8px" }, // ↓ was 14px 28px 10px
    footerContactRow: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: "16px", // ↓ was 20px
      marginBottom: "4px", // ↓ was 6px
    },
    footerContactItem: {
      display: "flex",
      alignItems: "center",
      gap: "5px",
      color: "rgba(255,255,255,0.95)",
      fontSize: "11.5px", // ↓ was 12.5px
      fontWeight: "500",
    },
    footerContactIcon: {
      width: "18px", // ↓ was 20px
      height: "18px",
      background: "rgba(255,255,255,0.22)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "10px", // ↓ was 11px
    },
    footerAddress: {
      textAlign: "center",
      color: "rgba(255,255,255,0.85)",
      fontSize: "10.5px", // ↓ was 11.5px
      marginTop: "3px",
    },
    footerImgStrip: { marginTop: "8px" }, // ↓ was 10px
    downloadWrap: {
      display: "flex",
      justifyContent: "center",
      padding: "14px 20px 18px", // ↓ was 18px 28px 24px
    },
    downloadBtn: {
      background: isDownloading ? "#9ca3af" : BRAND,
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      padding: "10px 36px", // ↓ was 12px 40px
      fontSize: "14px", // ↓ was 15px
      fontWeight: "700",
      cursor: isDownloading ? "not-allowed" : "pointer",
      letterSpacing: "0.5px",
      boxShadow: isDownloading ? "none" : "0 4px 14px rgba(91,175,214,0.45)",
      transition: "all 0.2s",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
  };

  return (
    <div style={styles.pageWrap}>
      <div style={styles.card}>
        <div className="receipt-inner-unique" style={{ minWidth: "300px" }}>
          {/* ── Header ── */}
          <div className="receipt-header-section" style={styles.header}>
            <div style={styles.headerTop}>
              <div style={styles.logoBox}>
                {!isZoneLoading && (
                  <img
                    src={
                      zoneData?.data
                        ? `data:image/png;base64,${zoneData.data}`
                        : "../assets/img/medicity_logo.png"
                    }
                    alt="Logo"
                    style={{
                      maxWidth: "180px", // ↓ was 200px
                      maxHeight: "60px", // ↓ was 75px
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                    }}
                    onError={(e) => {
                      e.target.src = "../assets/img/medicity_logo.png";
                    }}
                  />
                )}
              </div>
              <div style={styles.headerCenter}>
                <p style={styles.headerTitle}>Bill cum Cash Receipt</p>
                <p style={styles.headerSubtitle}>OFFICIAL PAYMENT DOCUMENT</p>
              </div>
              <div>
                <span style={styles.badgePill}>
                  #{" "}
                  {rowCashReceiptData?.appointment_payment_detail_obj[0]?.id ||
                    "—"}
                </span>
              </div>
            </div>
            <div style={styles.headerDivider} />
          </div>

          {/* ── Body ── */}
          <div style={styles.body}>
            <p style={styles.sectionLabel}>Patient &amp; Appointment Details</p>
            <div style={styles.infoGrid}>
              <div>
                {[
                  ["Patient Name", patient_obj?.patient_name || "—"],
                  [
                    "Age / Gender",
                    `${ageDetails?.years} Y / ${patient_obj?.gender || "—"}`,
                  ],
                  ["Contact No.", patient_obj?.mobile || "—"],
                  ["Referred By", rowCashReceiptData?.refer_doctor_name || "—"],
                  ["Address", patient_obj?.address || "—"],
                  ["Lab Name", rowCashReceiptData?.lab_name || "—"],
                ].map(([label, value]) => (
                  <div key={label} style={styles.infoRow}>
                    <span style={styles.infoLabel}>{label}</span>
                    <span style={styles.infoValue}>{value}</span>
                  </div>
                ))}
              </div>
              <div>
                {[
                  ["Reg No.", patient_obj?.registration_no || "—"],
                  ["Booking ID", rowCashReceiptData?.id || "—"],
                  [
                    "Patient Bill No.",
                    rowCashReceiptData?.appointment_payment_detail_obj[0]?.id ||
                      "—",
                  ],
                  [
                    "Visit / Reg. Date",
                    formatUnixTimestamp(patient_obj?.generated),
                  ],
                  [
                    "Client Code",
                    rowCashReceiptData?.franchise_obj?.franchise_code || "—",
                  ],
                  ["Center", zone_obj?.zone_name || "—"],
                ].map(([label, value]) => (
                  <div key={label} style={styles.infoRow}>
                    <span style={styles.infoLabel}>{label}</span>
                    <span style={styles.infoValue}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <p style={styles.sectionLabel}>Services</p>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    {[
                      "#",
                      "Service Code",
                      "Service Name",
                      "Department",
                      "Delivery Date",
                      "Total",
                    ].map((h) => (
                      <th key={h} style={styles.th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedData).map(
                    ([packageId, data], index) => {
                      const isAlt = index % 2 === 1;
                      return (
                        <tr
                          key={packageId}
                          style={isAlt ? { background: "#f7faff" } : {}}
                        >
                          <td style={styles.td}>{index + 1}</td>
                          <td style={styles.td}>
                            {data.investigations
                              .map((inv) => inv?.investigation_code)
                              .join(", ") || "—"}
                          </td>
                          <td style={styles.td}>
                            <div>
                              {data.package?.package_name && (
                                <strong
                                  style={{
                                    display: "block",
                                    color: BRAND,
                                    marginBottom: "1px", // ↓ was 2px
                                  }}
                                >
                                  {data.package.package_name}
                                </strong>
                              )}
                              <span
                                style={{ color: "#6b7280", fontSize: "11px" }} // ↓ was 12px
                              >
                                {data.investigations
                                  .map((inv) => inv?.investigation_name)
                                  .join(", ")}
                              </span>
                            </div>
                          </td>
                          <td style={styles.td}>
                            {Array.from(data.departments).join(", ") || "—"}
                          </td>
                          <td style={styles.td}>
                            {Array.from(data.delivery_times).join(", ") || "—"}
                          </td>
                          <td
                            style={{
                              ...styles.td,
                              fontWeight: "700",
                              color: BRAND,
                            }}
                          >
                            {data.package_cost || "—"}
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>

            {appointment_detail_obj[0]?.collection_charge > 0 && (
              <div style={{ marginBottom: "10px" }}>
                <p style={styles.sectionLabel}>Collection Charges</p>
                <div
                  style={{ ...styles.totalsBox, justifyContent: "flex-start" }}
                >
                  <div style={{ ...styles.totalsInner, maxWidth: "320px" }}>
                    {[
                      [
                        "Coll. Charges",
                        appointment_detail_obj[0]?.collection_charge || 0,
                      ],
                      [
                        "Received Coll. Charges",
                        appointment_detail_obj[0]?.collection_charge_received ||
                          0,
                      ],
                      [
                        "Balance Coll. Charges",
                        appointment_detail_obj[0]?.balance_collection_charge ||
                          0,
                      ],
                    ].map(([label, value]) => (
                      <div key={label} style={styles.totalRow}>
                        <span style={styles.totalLabel}>{label}</span>
                        <span style={styles.totalValue}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div style={styles.totalsBox}>
              <div style={styles.totalsInner}>
                {[
                  ["Bill Amount", billAmt || 0],
                  [
                    "Coll. Charges",
                    appointment_detail_obj[0]?.collection_charge || 0,
                  ],
                  [
                    "Total Discount",
                    appointment_detail_obj[0]?.discount_amount || 0,
                  ],
                  ["Total Paid Amount", billAmt || 0],
                ].map(([label, value]) => (
                  <div key={label} style={styles.totalRow}>
                    <span style={styles.totalLabel}>{label}</span>
                    <span style={styles.totalValue}>₹ {value}</span>
                  </div>
                ))}
                <div style={styles.totalRowFinal}>
                  <span>Due Amount</span>
                  <span>
                    ₹ {appointment_detail_obj[0]?.balance_amount || 0}
                  </span>
                </div>
              </div>
            </div>

            <div style={styles.wordsStrip}>
              💰 Received with thanks:{" "}
              {billAmt
                ? `Rupees ${capitalizeWords(converter.toWords(billAmt))} only`
                : "Rupees Zero only"}
            </div>

            <div style={styles.noteBox}>
              <strong>Note:</strong> This is a computer-generated Bill/Receipt
              and does not require any seal or signature.
            </div>

            <div style={styles.metaRow}>
              <div style={styles.metaItem}>
                <span>Bill Generated By:</span>
                <span style={styles.metaValue}>
                  {rowCashReceiptData?.created_by_name || "—"}
                </span>
              </div>
              <div style={styles.metaItem}>
                <span>Print Date:</span>
                <span style={styles.metaValue}>
                  {new Date().toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* ── Screen-only footer ── */}
          <div className="receipt-footer-section" style={styles.footer}>
            <div style={styles.footerContactRow}>
              {rowCashReceiptData?.zone_obj?.mobile_number && (
                <div style={styles.footerContactItem}>
                  <span style={styles.footerContactIcon}>📞</span>
                  <span>{rowCashReceiptData.zone_obj.mobile_number}</span>
                </div>
              )}
              {rowCashReceiptData?.zone_obj?.email && (
                <div style={styles.footerContactItem}>
                  <span style={styles.footerContactIcon}>✉</span>
                  <span>{rowCashReceiptData.zone_obj.email}</span>
                </div>
              )}
              {rowCashReceiptData?.zone_obj?.website && (
                <div style={styles.footerContactItem}>
                  <span style={styles.footerContactIcon}>🌐</span>
                  <span>{rowCashReceiptData.zone_obj.website}</span>
                </div>
              )}
            </div>
            {rowCashReceiptData?.zone_obj?.address && (
              <div style={styles.footerAddress}>
                📍 {rowCashReceiptData.zone_obj.address}
              </div>
            )}
            <div aria-hidden="true" style={styles.footerImgStrip}>
              <img
                src={Footer}
                alt="footer line"
                style={{
                  display: "block",
                  width: "100%",
                  margin: 0,
                  opacity: 0.6,
                }}
              />
            </div>
          </div>

          {/* ── Download button ── */}
          <div
            style={{ ...styles.downloadWrap, gap: "12px", flexWrap: "wrap" }}
          >
            <button
              className="download-btn"
              style={styles.downloadBtn}
              onClick={downloadPDF}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>⏳ Generating PDF...</>
              ) : (
                <>⬇️ Download with Header & Footer</>
              )}
            </button>

            <button
              className="download-btn-plain"
              style={{
                ...styles.downloadBtn,
                background: isDownloading ? "#9ca3af" : "#6b7280",
                boxShadow: isDownloading
                  ? "none"
                  : "0 4px 14px rgba(107,114,128,0.35)",
              }}
              onClick={downloadPDFWithoutHeaderFooter}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>⏳ Generating PDF...</>
              ) : (
                <>⬇️ Download without Header & Footer</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientReceiptCreditMode;
